<template>
  <div>
    <app-introduction
      :title="$t('campaignManager')"
      :user="currentUser"
      :documentations="[
        {
          link: $t('understandCampaignManagerLink'),
          description: $t('understandCampaignManagerDescription'),
        },
      ]"
    >
      <template v-slot:right>
        <el-switch
          v-if="isCampaignGroupingBetaAccount"
          v-model="showTerminated"
          :inactive-text="$t('showTerminated')"
          class="header-switch"
        >
        </el-switch>
        <el-switch
          v-if="currentUser.is_admin || currentUser.company_group_id == 8"
          v-model="isAdmin"
          inactive-text="Admin"
          class="header-switch"
        >
        </el-switch>
      </template>
    </app-introduction>
    <div class="container">
      <add-campaign-button
        v-if="campaignLinks && campaignLinks.create"
        @click="showAddCampaignDialog()"
        :isCampaign="true"
      >
      </add-campaign-button>
      <div
        class="campaigns"
        v-loading="isLoading"
      >
        <el-collapse
          v-if="isCampaignGroupingBetaAccount"
          v-model="activeSemesters"
          class="semester-collapse"
        >
          <el-collapse-item
            v-for="group in groupedCampaigns"
            :key="group.key"
            :name="group.key"
          >
            <template slot="title">
              <span class="semester-header">
                <span class="semester-label">{{ group.label }}</span>
                <span class="semester-count">{{
                  group.campaigns.length
                }}</span>
              </span>
            </template>
            <!-- Keep in sync with the flat-list <campaign> below (v-else) -->
            <campaign
              class="campaign"
              v-for="campaign in group.campaigns"
              v-bind:key="campaign.id"
              :data="campaign"
              :study-id="account.id"
              :admin="isAdmin"
              :currentUserIsAdmin="currentUser.is_admin"
              :study-url="account.url"
              :default-search-engine="defaultSearchEngine"
              :account="account"
              @delete-campaign="callAPI($event)"
              @update-campaign="
                (refreshedCampaign) =>
                  (campaign = Object.assign(campaign, refreshedCampaign))
              "
              @launch-campaign="callAPI($event)"
              @terminate-campaign="callAPI($event)"
            >
            </campaign>
          </el-collapse-item>
        </el-collapse>
        <template v-else>
          <!-- Keep in sync with the grouped <campaign> above (el-collapse branch) -->
          <campaign
            class="campaign"
            v-for="campaign in visibleCampaigns"
            v-bind:key="campaign.id"
            :data="campaign"
            :study-id="account.id"
            :admin="isAdmin"
            :currentUserIsAdmin="currentUser.is_admin"
            :study-url="account.url"
            :default-search-engine="defaultSearchEngine"
            :account="account"
            @delete-campaign="callAPI($event)"
            @update-campaign="
              (refreshedCampaign) =>
                (campaign = Object.assign(campaign, refreshedCampaign))
            "
            @launch-campaign="callAPI($event)"
            @terminate-campaign="callAPI($event)"
          >
          </campaign>
        </template>
      </div>
    </div>
    <add-campaign-dialog
      v-if="campaignCreateLinks"
      :link="campaignCreateLinks"
      v-model="addCampaignDialogVisible"
      @add-campaign="callAPI($event)"
      :study-id="account.id"
      :isCampaign="true"
    >
    </add-campaign-dialog>
  </div>
</template>

<script>
import AppIntroduction from "@/components/AppIntroduction";
import Campaign from "@/pages/CampaignManager/Campaign";
import AddCampaignDialog from "@/components/CampaignManager/AddCampaignDialog";
import AddCampaignButton from "@/components/CampaignManager/AddCampaignButton";
import { mapState } from "vuex";

const CAMPAIGN_GROUPING_ACCOUNT_IDS = [62212];

function semesterKeyFor(dateString) {
  if (!dateString) return "unknown";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "unknown";
  const year = date.getFullYear();
  const semester = date.getMonth() < 6 ? 1 : 2;
  return `${year}-S${semester}`;
}

function compareSemesterKeysDesc(semesterKeyA, semesterKeyB) {
  if (semesterKeyA === "unknown") return 1;
  if (semesterKeyB === "unknown") return -1;
  return semesterKeyB.localeCompare(semesterKeyA);
}

function semesterLabelFor(semesterKey, translate) {
  if (semesterKey === "unknown") return translate("unknownPeriod");
  const [year, semester] = semesterKey.split("-S");
  return translate("semesterLabel", { semester, year });
}

export default {
  components: {
    AppIntroduction,
    Campaign,
    AddCampaignDialog,
    AddCampaignButton,
  },
  props: {
    account: Object,
    currentUser: Object,
  },
  data() {
    return {
      addCampaignDialogVisible: false,
      campaigns: [],
      campaignLinks: null,
      campaignCreateLinks: null,
      isLoading: false,
      isAdmin: false,
      showTerminated: false,
      defaultSearchEngine: null,
      netlinkingCampaignsModule: null,
      activeSemesters: [],
      activeSemestersInitialized: false,
    };
  },
  methods: {
    showAddCampaignDialog() {
      this.addCampaignDialogVisible = true;
    },
    fetchCampaigns() {
      this.isLoading = true;
      const list = this.campaignLinks.list;
      this.$apiV2[list.meta.method.toLowerCase()](list.href)
        .then((response) => {
          this.campaigns = this.mergeCampaigns(this.campaigns, response.data.data);
          this.initializeActiveSemesters();
          this.isLoading = false;
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
        });
    },
    mergeCampaigns(existingCampaigns, freshCampaigns) {
      const existingById = new Map(
        existingCampaigns.map((campaign) => [campaign.id, campaign])
      );
      return freshCampaigns.map((freshCampaign) => {
        const existingCampaign = existingById.get(freshCampaign.id);
        const isUnchanged =
          existingCampaign &&
          JSON.stringify(existingCampaign) === JSON.stringify(freshCampaign);
        return isUnchanged ? existingCampaign : freshCampaign;
      });
    },
    initializeActiveSemesters() {
      if (this.activeSemestersInitialized) return;
      if (!this.groupedCampaigns.length) return;
      this.activeSemestersInitialized = true;
      const currentKey = semesterKeyFor(new Date().toISOString());
      const hasCurrentGroup = this.groupedCampaigns.some(
        (group) => group.key === currentKey
      );
      this.activeSemesters = hasCurrentGroup
        ? [currentKey]
        : [this.groupedCampaigns[0].key];
    },
    setConfigurations() {
      this.netlinkingCampaignsModule = this.studyLinks["netlinkingCampaigns"];
      this.defaultSearchEngine = this.study["searchEngines"].find(
        (searchEngine) => searchEngine.id == this.study["defaultSearchEngineId"]
      );
      this.callNetlinkingCampaignsModuleAPI();
    },
    callAPI(details) {
      this.$apiV2[details.link.method.toLowerCase()](
        details.link.href,
        details.campaign
      )
        .then((response) => {
          this.fetchCampaigns();
        })
        .catch((error) => {
          if (error.response.data.hasOwnProperty("errors")) {
            const missingFieldError = error.response.data.errors.find(
              (error) => error.code == "MISSING_FIELDS"
            );

            if (missingFieldError && missingFieldError.meta) {
              let htmlMessageMissingFields =
                "<p>" + this.$i18n.t("missingFields") + " :" + "</p>" + "<ul>";
              missingFieldError.meta.forEach((field) => {
                htmlMessageMissingFields =
                  htmlMessageMissingFields +
                  "<li>" +
                  this.$i18n.t(field) +
                  "</li>";
              });
              htmlMessageMissingFields = htmlMessageMissingFields + "</ul>";

              this.$alert(
                htmlMessageMissingFields,
                this.$i18n.t("missingFieldsTitle"),
                {
                  confirmButtonText: "OK",
                  dangerouslyUseHTMLString: true,
                  type: "error",
                }
              );
            }
          }
          console.log(error);
        });
    },
    callNetlinkingCampaignsModuleAPI() {
      let netlinkingModule = this.netlinkingCampaignsModule;
      this.$apiV2[netlinkingModule.method.toLowerCase()](
        netlinkingModule.href,
        {
          headers: {
            Accept: "application/vnd.api+json",
            "Accept-Language": this.$i18n.locale,
          },
        }
      )
        .then((response) => {
          this.campaignLinks = response.data.data.attributes;
          this.mapToCampaignCreateLinks(this.campaignLinks.create);
          this.fetchCampaigns();
        })
        .catch((error) => {
          console.error(error);
        });
    },
    mapToCampaignCreateLinks(createLink) {
      this.campaignCreateLinks = {
        form: createLink.meta.form,
        method: createLink.meta.method,
        href: createLink.href,
      };
    },
  },
  created() {
    this.setConfigurations();
  },
  computed: {
    ...mapState(["study", "studyLinks"]),
    isCampaignGroupingBetaAccount() {
      return CAMPAIGN_GROUPING_ACCOUNT_IDS.includes(
        Number(this.account.id)
      );
    },
    visibleCampaigns() {
      if (!this.isCampaignGroupingBetaAccount) return this.campaigns;
      return this.campaigns.filter(
        (campaign) => !campaign.isTerminated || this.showTerminated
      );
    },
    groupedCampaigns() {
      const groups = {};
      this.visibleCampaigns.forEach((campaign) => {
        const key = semesterKeyFor(campaign.period && campaign.period.end);
        if (!groups[key]) groups[key] = [];
        groups[key].push(campaign);
      });
      return Object.keys(groups)
        .sort(compareSemesterKeysDesc)
        .map((key) => ({
          key,
          label: semesterLabelFor(key, this.$t.bind(this)),
          campaigns: groups[key],
        }));
    },
  },
};
</script>
<style lang="scss" scoped>
@use "styles/variables" as *;

.header-switch {
  margin-right: 2rem;
  margin-bottom: 0.5rem;
}

.container {
  .campaigns {
    min-height: 100px;
    margin-bottom: 2rem;

    ::v-deep .semester-collapse {
      border-top: none;
      border-bottom: none;

      > .el-collapse-item {
        margin-bottom: 0.75rem;
        border: 1px solid #ebeef5;
        border-radius: 4px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
        overflow: hidden;

        &:last-child {
          margin-bottom: 0;
        }

        > div > .el-collapse-item__header {
          height: 48px;
          padding: 0 16px;
          background-color: #fff;
          border-bottom-color: #ebeef5;
        }

        > .el-collapse-item__wrap {
          background-color: transparent;

          > .el-collapse-item__content {
            padding: 1rem 16px 0;
          }
        }
      }
    }

    .campaign {
      margin-bottom: 1rem;
    }
  }
}

.semester-header {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
}

.semester-label {
  font-size: 1rem;
  font-weight: 600;
  line-height: 24px;
  color: $sk-color-text-primary;
}

.semester-count {
  margin-right: 0.75rem;
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 24px;
  color: $sk-color-text-secondary;
}
</style>

<i18n>
{
  "en": {
    "campaignManager": "Netlinking campaign manager",
    "showTerminated": "Show terminated",
    "missingFields": "Thoses field are required",
    "missingFieldsTitle": "There are empty fields",
    "name": "Name",
    "companyName": "Company Name",
    "activitySector": "Theme",
    "companyDescription": "Company Description",
    "highlights": "Values to highlight",
    "links": "Additional Links (image, vidéo, etc)",
    "status": "Status",
    "budget": "Budget",
    "description": "Description",
    "period": "Period",
    "unknownPeriod": "Unknown period",
    "semesterLabel": "{year} - Semester {semester}",
    "budgetWithoutMargin": "Budget without margin",
    "backlinkTypes": "Backlink types",
    "understandCampaignManagerLink": "http://docs.smartkeyword.io/fr/articles/5126760-comprendre-le-campaign-manager",
    "understandCampaignManagerDescription": "Know more about our backlinks quality"
  },
  "fr": {
    "campaignManager": "Gestion de campagne netlinking",
    "showTerminated": "Afficher les terminées",
    "missingFields": "Ces champs sont obligatoires",
    "missingFieldsTitle": "Certains champs ne sont pas renseignés",
    "name": "Nom",
    "companyName": "Nom de l'entreprise",
    "activitySector": "Thématique",
    "companyDescription": "Description de l'entreprise",
    "highlights": "Proposition de valeurs",
    "links": "Liens complémentaires (image, vidéo, etc)",
    "status": "Statut",
    "budget": "Budget",
    "description": "Description",
    "period": "Période",
    "unknownPeriod": "Période inconnue",
    "semesterLabel": "{year} - Semestre {semester}",
    "budgetWithoutMargin": "Budget sans les marges",
    "backlinkTypes": "Types de backlinks",
    "understandCampaignManagerLink": "http://docs.smartkeyword.io/fr/articles/5126760-comprendre-le-campaign-manager",
    "understandCampaignManagerDescription": "Comprendre le Campaign Manager"
  },
  "de": {
    "campaignManager": "Verwaltung von Netlinking-Kampagnen",
    "showTerminated": "Beendete anzeigen",
    "missingFields": "Diese Felder sind erforderlich",
    "missingFieldsTitle": "Einige Felder sind nicht ausgefüllt",
    "name": "Name",
    "companyName": "Name der Firma",
    "activitySector": "Thematisch",
    "companyDescription": "Firmen Beschreibung",
    "highlights": "Wertversprechen",
    "links": "Weiterführende Links (Bild, Video etc.)",
    "status": "Status",
    "budget": "Budget",
    "description": "Beschreibung",
    "period": "Zeitraum",
    "unknownPeriod": "Unbekannter Zeitraum",
    "semesterLabel": "{year} - Semester {semester}",
    "budgetWithoutMargin": "Budget ohne Margen",
    "backlinkTypes": "Arten von Backlinks",
    "understandCampaignManagerLink": "http://docs.smartkeyword.io/fr/articles/5126760-comprendre-le-campaign-manager",
    "understandCampaignManagerDescription": "Campaign Manager verstehen"
  }
}
</i18n>
